import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getV2ProviderPatientsPayerStats: build.query<
      GetV2ProviderPatientsPayerStatsApiResponse,
      GetV2ProviderPatientsPayerStatsApiArg
    >({
      query: () => ({ url: `/v2/provider/patients/payer-stats` }),
    }),
    getV2ProviderPatientsByPatientIdCoverages: build.query<
      GetV2ProviderPatientsByPatientIdCoveragesApiResponse,
      GetV2ProviderPatientsByPatientIdCoveragesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/coverages`,
        params: {
          include_inactive: queryArg.includeInactive,
        },
      }),
    }),
    postV2ProviderPatientsByPatientIdCoverages: build.mutation<
      PostV2ProviderPatientsByPatientIdCoveragesApiResponse,
      PostV2ProviderPatientsByPatientIdCoveragesApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/coverages`,
        method: "POST",
        body: queryArg.createCoverageRequest,
      }),
    }),
    deleteV2ProviderPatientsByPatientIdCoveragesAndCoverageId: build.mutation<
      DeleteV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiResponse,
      DeleteV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/coverages/${queryArg.coverageId}`,
        method: "DELETE",
      }),
    }),
    getV2ProviderPatientsByPatientIdCoveragesAndCoverageId: build.query<
      GetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiResponse,
      GetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/coverages/${queryArg.coverageId}`,
      }),
    }),
    putV2ProviderPatientsByPatientIdCoveragesAndCoverageId: build.mutation<
      PutV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiResponse,
      PutV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/coverages/${queryArg.coverageId}`,
        method: "PUT",
        body: queryArg.updateCoverageRequest,
      }),
    }),
    postV2ProviderPatientsByPatientIdInsuranceCard: build.mutation<
      PostV2ProviderPatientsByPatientIdInsuranceCardApiResponse,
      PostV2ProviderPatientsByPatientIdInsuranceCardApiArg
    >({
      query: (queryArg) => ({
        url: `/v2/provider/patients/${queryArg.patientId}/insurance-card`,
        method: "POST",
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as enhancedApi };
export type GetV2ProviderPatientsPayerStatsApiResponse =
  /** status 200 OK */ PayerStatsResponse;
export type GetV2ProviderPatientsPayerStatsApiArg = void;
export type GetV2ProviderPatientsByPatientIdCoveragesApiResponse =
  /** status 200 OK */ CoveragesListResponse;
export type GetV2ProviderPatientsByPatientIdCoveragesApiArg = {
  patientId: string;
  /** Include expired/cancelled coverages (insurance history) in addition to active/pending ones. */
  includeInactive?: boolean;
};
export type PostV2ProviderPatientsByPatientIdCoveragesApiResponse =
  /** status 201 Created */ CoverageResponse2;
export type PostV2ProviderPatientsByPatientIdCoveragesApiArg = {
  patientId: string;
  createCoverageRequest: CreateCoverageRequest;
};
export type DeleteV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiResponse =
  /** status 200 OK */ SuccessResponse;
export type DeleteV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiArg = {
  patientId: string;
  coverageId: string;
};
export type GetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiResponse =
  /** status 200 OK */ CoverageResponse2;
export type GetV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiArg = {
  patientId: string;
  coverageId: string;
};
export type PutV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiResponse =
  /** status 200 OK */ CoverageResponse2;
export type PutV2ProviderPatientsByPatientIdCoveragesAndCoverageIdApiArg = {
  patientId: string;
  coverageId: string;
  updateCoverageRequest: UpdateCoverageRequest;
};
export type PostV2ProviderPatientsByPatientIdInsuranceCardApiResponse =
  /** status 200 OK */ InsuranceCardExtractionResponseSchema;
export type PostV2ProviderPatientsByPatientIdInsuranceCardApiArg = {
  patientId: string;
};
export type PayerStatRow = {
  /** Payer logo URL */
  avatar_url?: string | null;
  /** Number of patients with this payer */
  patient_count: number;
  /** Payer display name */
  payer_name: string;
  /** Internal payer UUID */
  payer_uuid: string;
};
export type PayerStatsResponse = {
  /** Patients with coverages needing payer review (overlaps with payer rows) */
  needs_review_count: number;
  /** Patients with no active or pending coverage */
  no_coverage_count: number;
  /** Resolved payers sorted by patient count descending */
  payers: PayerStatRow[];
  /** Patients explicitly designated as self-pay */
  self_pay_count: number;
  /** Total patients across payer rows, self-pay, and no-coverage */
  total_patients: number;
};
export type ClientErrorResponse = {
  /** The error code for the error */
  error_code?: string;
  /** The message for the error */
  message?: string;
  /** The HTTP status code for the client error */
  status?: number;
};
export type ValidationErrorElement = {
  ctx?: object;
  loc: string[];
  msg: string;
  type: string;
};
export type ValidationError = ValidationErrorElement[];
export type ClaimFilingIndicatorCode =
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "AM"
  | "BL"
  | "CH"
  | "CI"
  | "DS"
  | "FI"
  | "HM"
  | "LM"
  | "MA"
  | "MB"
  | "MC"
  | "OF"
  | "TV"
  | "VA"
  | "WC"
  | "ZZ";
export type PatientAddress = {
  /** City name */
  city?: string | null;
  /** Street address line 1 */
  line1?: string | null;
  /** Street address line 2 (apartment, suite, etc.) */
  line2?: string | null;
  /** Optional landline/location phone in E.164 format (e.g., +12125551234) */
  phone?: string | null;
  /** State abbreviation (2 characters) */
  state?: string | null;
  /** ZIP code (5 or 9 digit format, e.g., 12345 or 12345-6789) */
  zip?: string | null;
};
export type CoverageResponse = {
  claim_filing_code?: ClaimFilingIndicatorCode | null;
  consent_document_url?: string | null;
  consent_on_file: boolean;
  coverage_order: number;
  created_at?: string | null;
  /** Presentation status derived from stored status + date window vs today (GDN-2411): 'active', 'pending', 'expired', 'future', or 'cancelled'. A stored-active coverage past its termination date displays as 'expired'. */
  display_status?: string | null;
  effective_date?: string | null;
  group_number?: string | null;
  kind: string;
  member_id?: string | null;
  /** List of field names that are required but missing (e.g., ['member_id']). UI should highlight these fields. */
  missing_fields?: string[] | null;
  organization_id: string;
  patient_id: string;
  patient_insurance_coverage_id: string;
  payer_id?: string | null;
  payer_name?: string | null;
  /** True when payer was resolved with low confidence or unresolved; requires manual verification. Null for manually-entered coverages. */
  payer_resolution_needs_review?: boolean | null;
  payer_uuid?: string | null;
  plan_name?: string | null;
  relationship_to_subscriber: string;
  status: string;
  subscriber_address?: PatientAddress | null;
  subscriber_dob?: string | null;
  subscriber_first_name?: string | null;
  subscriber_last_name?: string | null;
  subscriber_sex?: string | null;
  termination_date?: string | null;
  updated_at?: string | null;
};
export type CoverageGapSchema = {
  /** Last day of the gap; null = no later coverage on file */
  end?: string | null;
  /** First day with no billable coverage */
  start: string;
};
export type CoveragesListResponse = {
  /** Resolved bill-with-JotPsych flag for this patient (org routing rules) */
  bill_with_jotpsych_resolved?: boolean | null;
  /** Source of the resolved flag: 'org_payer_rule' or 'org_default' */
  bill_with_jotpsych_source?: string | null;
  /** True when a billable (active-status) coverage window contains today (GDN-2411 / D5). Scheduling surfaces use this to badge patients with no coverage in force. */
  coverage_in_force_today?: boolean | null;
  coverages: CoverageResponse[];
  /** The current or next window with no billable coverage in force, if any. Null when coverage is continuous/open-ended. */
  next_coverage_gap?: CoverageGapSchema | null;
  total: number;
};
export type ClaimFilingIndicatorCode2 =
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "AM"
  | "BL"
  | "CH"
  | "CI"
  | "DS"
  | "FI"
  | "HM"
  | "LM"
  | "MA"
  | "MB"
  | "MC"
  | "OF"
  | "TV"
  | "VA"
  | "WC"
  | "ZZ";
export type PatientAddress2 = {
  /** City name */
  city?: string | null;
  /** Street address line 1 */
  line1?: string | null;
  /** Street address line 2 (apartment, suite, etc.) */
  line2?: string | null;
  /** Optional landline/location phone in E.164 format (e.g., +12125551234) */
  phone?: string | null;
  /** State abbreviation (2 characters) */
  state?: string | null;
  /** ZIP code (5 or 9 digit format, e.g., 12345 or 12345-6789) */
  zip?: string | null;
};
export type CoverageResponse2 = {
  claim_filing_code?: ClaimFilingIndicatorCode2 | null;
  consent_document_url?: string | null;
  consent_on_file: boolean;
  coverage_order: number;
  created_at?: string | null;
  /** Presentation status derived from stored status + date window vs today (GDN-2411): 'active', 'pending', 'expired', 'future', or 'cancelled'. A stored-active coverage past its termination date displays as 'expired'. */
  display_status?: string | null;
  effective_date?: string | null;
  group_number?: string | null;
  kind: string;
  member_id?: string | null;
  /** List of field names that are required but missing (e.g., ['member_id']). UI should highlight these fields. */
  missing_fields?: string[] | null;
  organization_id: string;
  patient_id: string;
  patient_insurance_coverage_id: string;
  payer_id?: string | null;
  payer_name?: string | null;
  /** True when payer was resolved with low confidence or unresolved; requires manual verification. Null for manually-entered coverages. */
  payer_resolution_needs_review?: boolean | null;
  payer_uuid?: string | null;
  plan_name?: string | null;
  relationship_to_subscriber: string;
  status: string;
  subscriber_address?: PatientAddress2 | null;
  subscriber_dob?: string | null;
  subscriber_first_name?: string | null;
  subscriber_last_name?: string | null;
  subscriber_sex?: string | null;
  termination_date?: string | null;
  updated_at?: string | null;
};
export type ClaimFilingIndicatorCode3 =
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "AM"
  | "BL"
  | "CH"
  | "CI"
  | "DS"
  | "FI"
  | "HM"
  | "LM"
  | "MA"
  | "MB"
  | "MC"
  | "OF"
  | "TV"
  | "VA"
  | "WC"
  | "ZZ";
export type PatientAddress3 = {
  /** City name */
  city?: string | null;
  /** Street address line 1 */
  line1?: string | null;
  /** Street address line 2 (apartment, suite, etc.) */
  line2?: string | null;
  /** Optional landline/location phone in E.164 format (e.g., +12125551234) */
  phone?: string | null;
  /** State abbreviation (2 characters) */
  state?: string | null;
  /** ZIP code (5 or 9 digit format, e.g., 12345 or 12345-6789) */
  zip?: string | null;
};
export type CreateCoverageRequest = {
  /** X12 claim filing indicator override (e.g. 'FI' for BCBS FEP); NULL = auto-derive */
  claim_filing_code?: ClaimFilingIndicatorCode3 | null;
  /** S3 URL of consent document */
  consent_document_url?: string | null;
  /** Authorization signature on file */
  consent_on_file?: boolean;
  /** Coverage priority: 1=primary, 2=secondary, 3=tertiary */
  coverage_order?: number;
  /** Coverage start date */
  effective_date?: string | null;
  /** Group/employer number */
  group_number?: string | null;
  /** Coverage kind: insurance, self_pay, other */
  kind?: string;
  /** Member/policy ID on insurance card (required for active coverages) */
  member_id?: string | null;
  /** External payer identifier */
  payer_id?: string | null;
  /** Payer display name */
  payer_name?: string | null;
  /** Plan name */
  plan_name?: string | null;
  /** Relationship to subscriber: self, spouse, child, other */
  relationship_to_subscriber?: string;
  /** DocRef that carried the extraction this coverage was created from. Pair with source_extraction_kind. */
  source_document_reference_id?: string | null;
  /** Key inside docref.metadata.extractions — e.g. 'insurance_card'. Pair with source_document_reference_id. */
  source_extraction_kind?: string | null;
  /** Coverage status */
  status?: string;
  /** Subscriber address (only if relationship != 'self') */
  subscriber_address?: PatientAddress3 | null;
  /** Subscriber DOB (only if relationship != 'self') */
  subscriber_dob?: string | null;
  /** Subscriber first name (only if relationship != 'self') */
  subscriber_first_name?: string | null;
  /** Subscriber last name (only if relationship != 'self') */
  subscriber_last_name?: string | null;
  /** Subscriber sex: Male/Female/Unknown (only if relationship != 'self') */
  subscriber_sex?: string | null;
  /** Coverage end date */
  termination_date?: string | null;
};
export type SuccessResponse = {
  /** Whether the operation succeeded */
  success?: boolean;
};
export type ClaimFilingIndicatorCode4 =
  | "11"
  | "12"
  | "13"
  | "14"
  | "15"
  | "16"
  | "17"
  | "AM"
  | "BL"
  | "CH"
  | "CI"
  | "DS"
  | "FI"
  | "HM"
  | "LM"
  | "MA"
  | "MB"
  | "MC"
  | "OF"
  | "TV"
  | "VA"
  | "WC"
  | "ZZ";
export type PatientAddress4 = {
  /** City name */
  city?: string | null;
  /** Street address line 1 */
  line1?: string | null;
  /** Street address line 2 (apartment, suite, etc.) */
  line2?: string | null;
  /** Optional landline/location phone in E.164 format (e.g., +12125551234) */
  phone?: string | null;
  /** State abbreviation (2 characters) */
  state?: string | null;
  /** ZIP code (5 or 9 digit format, e.g., 12345 or 12345-6789) */
  zip?: string | null;
};
export type UpdateCoverageRequest = {
  claim_filing_code?: ClaimFilingIndicatorCode4 | null;
  consent_document_url?: string | null;
  consent_on_file?: boolean | null;
  coverage_order?: number | null;
  effective_date?: string | null;
  group_number?: string | null;
  kind?: string | null;
  member_id?: string | null;
  payer_id?: string | null;
  payer_name?: string | null;
  plan_name?: string | null;
  relationship_to_subscriber?: string | null;
  status?: string | null;
  subscriber_address?: PatientAddress4 | null;
  subscriber_dob?: string | null;
  subscriber_first_name?: string | null;
  subscriber_last_name?: string | null;
  subscriber_sex?: string | null;
  termination_date?: string | null;
};
export type InsuranceCardExtractionResponseSchema = {
  card_image_url_back?: string | null;
  card_image_url_front?: string | null;
  confidence?: number;
  effective_date?: string | null;
  group_number?: string | null;
  member_id?: string | null;
  payer_name?: string | null;
  plan_name?: string | null;
  subscriber_first_name?: string | null;
  subscriber_last_name?: string | null;
  subscriber_relationship?: string | null;
};
